/**
 * Deeplink Generation for Mobile Integration
 *
 * Generates platform-specific deeplinks for iOS and Android mobile flows.
 * Supports the two-phase authentication pattern where biometric capture
 * happens in the native app (Phase 1) followed by WebAuthn ceremony (Phase 2).
 *
 * @module core/deeplink
 */

import type { Platform } from '../utils/platform';
import type { CustomOptions } from './encoder';

/**
 * Parameters for enrollment deeplink generation
 */
export interface EnrollmentDeeplinkParams {
  /**
   * Username for enrollment
   */
  username: string;

  /**
   * Relying Party domain (e.g., 'example.com')
   */
  rp: string;

  /**
   * Return URL for callback after biometric capture
   */
  returnURL: string;

  /**
   * Policy ID for authenticator configuration
   */
  policyId: string;

  /**
   * API key for backend authentication
   */
  apiKey: string;

  /**
   * Optional user ID (base64-encoded).
   *
   * When omitted this is derived as base64(UTF-8(username)), matching the native launcher
   * (the native iOS launcher falls back to the UTF-8 username when no userId is given). The app derives
   * the same value itself when the param is absent, so this only removes ambiguity.
   */
  userId?: string;

  /**
   * Human-readable display name for the credential.
   *
   * Sent for parity with the native launcher, which includes it when provided. Note the app's
   * deeplink router does not currently read `displayName` on any host — it is accepted and
   * ignored — so this is forward-compatibility, not behaviour you can depend on yet.
   */
  displayName?: string;

  /**
   * Public key that unlocks `encryptedEmbedding` on the result, enabling local silent
   * re-verification. The app stores it for the ceremony
   * (`SharedStorage.saveVerifyPublicKey`).
   *
   * This SDK has no key manager, so the caller supplies the key; without it enrollment still
   * succeeds but no encrypted embedding is ever returned.
   */
  verifyPublicKey?: string;

  /**
   * Caller-supplied age, used by the app to adapt enrollment — for instance skipping the
   * document scan for under-16 users.
   */
  userAge?: number;

  /**
   * iOS UltraPass URL scheme without `://` (e.g. 'ultrapass' or 'ultrapass-staging').
   *
   * Must match the UltraPass build installed on the device. Defaults to 'ultrapass'.
   */
  scheme?: string;
}

/**
 * Parameters for the unified `start` deeplink.
 *
 * `ultrapass://start` is the current entry point on iOS — UltraPass decides enroll-vs-verify
 * itself by looking for a matching local credential, then reports back which one it ran by
 * appending `mode=verify` or `mode=enroll` to the return URL
 * (the app's `start` deeplink branch).
 *
 * Prefer this over the legacy `ultrapass://verify` host for authentication: the `start` branch
 * reads `options` (in the app's start branch) and routes into the same `.authenticate` flow as the
 * legacy path, so custom biometric options are fully honoured. The newer
 * `ultrapass://verify?mode=verify` branch does **not** read `options` — it only understands
 * `verifyMethod` — so migrating authentication there would silently drop them.
 */
export interface StartDeeplinkParams {
  /**
   * Relying Party ID (domain)
   */
  rpId: string;

  /**
   * Return URL for callback. UltraPass appends `mode=verify` or `mode=enroll` to this.
   */
  returnURL: string;

  /**
   * Policy ID for authenticator configuration
   */
  policyId: string;

  /**
   * API key for backend authentication
   */
  apiKey: string;

  /**
   * Username to match a local credential against
   */
  username?: string;

  /**
   * Optional user ID (base64-encoded)
   */
  userId?: string;

  /**
   * Candidate credential IDs. Sent as the `credentialIds` comma-separated list the current
   * app expects, not the legacy singular `credentialId`.
   */
  credentialIds?: string[];

  /**
   * Custom biometric options. Encoded to the same 32-byte base64 payload the app validates
   * (32 bytes, leading `0xFF`) — see the app's start-branch options decoding.
   */
  options?: CustomOptions;

  /**
   * When true, sends `verifyOnly=true` so UltraPass returns
   * `status=error&reason=credential_invalid` instead of silently falling back to enrollment
   * when no local credential matches.
   *
   * Authentication sets this — an explicit sign-in should fail rather than enrol.
   */
  verifyOnly?: boolean;

  /**
   * Pre-select the verification modality, skipping UltraPass's auth-method picker.
   * Only 'face' and 'voice' are recognised by the app.
   */
  verifyMethod?: 'face' | 'voice';

  /**
   * Public key that unlocks `encryptedEmbedding` on the result, for local re-verification.
   */
  verifyPublicKey?: string;

  /**
   * Caller-supplied age, used by the app to adapt enrollment (e.g. skipping document scan
   * for under-16 users).
   */
  userAge?: number;

  /**
   * iOS UltraPass URL scheme without `://` (e.g. 'ultrapass' or 'ultrapass-staging').
   *
   * Must match the UltraPass build installed on the device. Defaults to 'ultrapass'.
   */
  scheme?: string;
}

/**
 * Parameters for verification deeplink generation
 */
export interface VerificationDeeplinkParams {
  /**
   * Relying Party ID (domain)
   */
  rpId: string;

  /**
   * Return URL for callback after biometric capture
   */
  returnURL: string;

  /**
   * Policy ID for authenticator configuration
   */
  policyId: string;

  /**
   * API key for backend authentication
   */
  apiKey: string;

  /**
   * Optional credential ID to verify against
   */
  credentialId?: string;

  /**
   * Optional user ID
   */
  userId?: string;

  /**
   * Optional custom verification options
   */
  options?: CustomOptions;

  /**
   * Verification mode for backend token verification
   * - 'default': Verify with backend (default behavior)
   * - 'none': Skip backend verification (JWE token only)
   */
  verification?: 'default' | 'none';

  /**
   * iOS UltraPass URL scheme without `://` (e.g. 'ultrapass' or 'ultrapass-staging').
   *
   * Must match the UltraPass build installed on the device. Defaults to 'ultrapass'.
   */
  scheme?: string;
}

/**
 * Generates iOS enrollment deeplink
 *
 * Format: {scheme}://fido2-enroll?username={}&rp={}&returnURL={}&userId={}&displayName={}
 *         &policyId={}&apiKey={}&verifyPublicKey={}&userAge={}
 *
 * Parameter set matches the native launcher's enroll deeplink
 * (the native iOS launcher uses the `fido2-enroll` host).
 *
 * @param params - Enrollment parameters
 * @returns iOS deeplink URL
 *
 * @example
 * ```typescript
 * const deeplink = generateIOSEnrollmentDeeplink({
 *   username: 'john.doe',
 *   rp: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   policyId: 'mobile',
 *   apiKey: 'your-api-key',
 *   displayName: 'John Doe'
 * });
 * ```
 */
export function generateIOSEnrollmentDeeplink(params: EnrollmentDeeplinkParams): string {
  const queryParams = new URLSearchParams();
  queryParams.set('username', params.username);
  queryParams.set('rp', params.rp);
  queryParams.set('returnURL', params.returnURL);
  queryParams.set('policyId', params.policyId);
  queryParams.set('apiKey', params.apiKey);

  // Native always sends userId, deriving it from the username when the caller does not supply
  // one. The app performs the same derivation for an absent param, so matching here keeps the
  // two implementations byte-comparable rather than relying on that fallback.
  const userId = params.userId || deriveUserIdFromUsername(params.username);
  if (userId) {
    queryParams.set('userId', userId);
  }

  if (params.displayName) {
    queryParams.set('displayName', params.displayName);
  }

  if (params.verifyPublicKey) {
    queryParams.set('verifyPublicKey', params.verifyPublicKey);
  }

  if (params.userAge !== undefined) {
    queryParams.set('userAge', String(params.userAge));
  }

  return `${params.scheme || 'ultrapass'}://fido2-enroll?${queryParams.toString()}`;
}

/**
 * Derives a userId the same way the native launcher and app do: base64 of the UTF-8 username.
 *
 * Returns undefined for an empty username, and for any value that cannot be encoded, so the
 * caller simply omits the param and lets the app fall back to its own derivation.
 *
 * @param username - Username to derive from
 * @returns Base64-encoded userId, or undefined
 */
function deriveUserIdFromUsername(username: string): string | undefined {
  if (!username) {
    return undefined;
  }

  try {
    const bytes = new TextEncoder().encode(username);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  } catch {
    return undefined;
  }
}

/**
 * Generates the unified iOS `start` deeplink.
 *
 * Format: {scheme}://start?rpId={}&returnURL={}&policyId={}&apiKey={}&username={}
 *         &credentialIds={csv}&options={base64}&verifyOnly={}&verifyMethod={}
 *
 * UltraPass resolves enroll-vs-verify itself and reports the outcome back as `mode` on the
 * return URL, which removes the need for the browser to commit to an operation up front.
 *
 * @param params - Start parameters
 * @returns iOS deeplink URL
 *
 * @example
 * ```typescript
 * const deeplink = generateIOSStartDeeplink({
 *   rpId: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   policyId: 'mobile',
 *   apiKey: 'your-api-key',
 *   username: 'john.doe',
 *   credentialIds: ['AAA...', 'BBB...'],
 *   verifyOnly: true,
 *   options: { checkLiveness: true, checkAge: true, ageThreshold: 21 }
 * });
 * ```
 */
export function generateIOSStartDeeplink(params: StartDeeplinkParams): string {
  const queryParams = new URLSearchParams();
  queryParams.set('rpId', params.rpId);
  queryParams.set('returnURL', params.returnURL);
  queryParams.set('policyId', params.policyId);
  queryParams.set('apiKey', params.apiKey);

  if (params.username) {
    queryParams.set('username', params.username);
  }

  if (params.userId) {
    queryParams.set('userId', params.userId);
  }

  // The app expects a comma-separated list under `credentialIds`. Empty entries are dropped
  // on its side, so filter them here rather than emitting a trailing comma.
  const credentialIds = params.credentialIds?.filter(id => !!id);
  if (credentialIds && credentialIds.length > 0) {
    queryParams.set('credentialIds', credentialIds.join(','));
  }

  if (params.options) {
    queryParams.set('options', encodeOptionsForIOS(params.options));
  }

  if (params.verifyOnly) {
    queryParams.set('verifyOnly', 'true');
  }

  if (params.verifyMethod) {
    queryParams.set('verifyMethod', params.verifyMethod);
  }

  if (params.verifyPublicKey) {
    queryParams.set('verifyPublicKey', params.verifyPublicKey);
  }

  if (params.userAge !== undefined) {
    queryParams.set('userAge', String(params.userAge));
  }

  return `${params.scheme || 'ultrapass'}://start?${queryParams.toString()}`;
}

/**
 * Generates iOS verification deeplink
 *
 * Format: {scheme}://verify?rpId={}&returnURL={}&policyId={}&apiKey={}&credentialId={}&userId={}&options={}
 *
 * @param params - Verification parameters
 * @returns iOS deeplink URL
 *
 * @example
 * ```typescript
 * const deeplink = generateIOSVerificationDeeplink({
 *   rpId: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   policyId: 'mobile',
 *   apiKey: 'your-api-key',
 *   options: {
 *     checkLiveness: true,
 *     checkAge: true,
 *     ageThreshold: 21
 *   }
 * });
 * ```
 */
export function generateIOSVerificationDeeplink(params: VerificationDeeplinkParams): string {
  const queryParams = new URLSearchParams();
  queryParams.set('rpId', params.rpId);
  queryParams.set('returnURL', params.returnURL);
  queryParams.set('policyId', params.policyId);
  queryParams.set('apiKey', params.apiKey);

  if (params.credentialId) {
    queryParams.set('credentialId', params.credentialId);
  }

  if (params.userId) {
    queryParams.set('userId', params.userId);
  }

  if (params.options) {
    // Encode options as Base64 JSON for iOS
    const optionsBase64 = encodeOptionsForIOS(params.options);
    queryParams.set('options', optionsBase64);
  }

  return `${params.scheme || 'ultrapass'}://verify?${queryParams.toString()}`;
}

/**
 * Generates Android initialization deeplink
 *
 * Format: privateid://init?sessionToken={}&policyId={}
 *
 * @param apiKey - API key (used as sessionToken)
 * @param policyId - Policy ID
 * @returns Android deeplink URL
 *
 * @example
 * ```typescript
 * const deeplink = generateAndroidInitDeeplink('your-api-key', 'mobile');
 * ```
 */
export function generateAndroidInitDeeplink(apiKey: string, policyId: string): string {
  const queryParams = new URLSearchParams();
  queryParams.set('sessionToken', apiKey);
  queryParams.set('policyId', policyId);

  return `privateid://init?${queryParams.toString()}`;
}

/**
 * Action values understood by Android's `SessionInitActivity`.
 *
 * Only the preflight is exposed here; the others are listed for reference.
 * Source: `SessionInitActivity.kt` companion constants.
 */
export const ANDROID_ACTIONS = {
  RESOLVE_PROFILE_AND_CREDENTIALS: 'resolve_profile_and_credentials',
  VERIFY_CREDENTIALS: 'verify_credentials',
  VERIFY_IDENTITY: 'verify_identity',
  ANONYMOUS_VERIFY: 'anonymous_verify',
  NONE: 'none'
} as const;

/**
 * Parameters for the Android resolve preflight.
 */
export interface AndroidResolveParams {
  /**
   * API key, sent as `sessionToken`
   */
  apiKey: string;

  /**
   * Policy ID
   */
  policyId: string;

  /**
   * Relying Party ID (domain). Required — the app ignores the action without it.
   */
  rpId: string;

  /**
   * Return URL the app redirects to with the outcome. Required for the same reason.
   */
  returnURL: string;

  /**
   * Candidate credential IDs to look for on the device. Sent as `credentialIdList` (CSV).
   */
  credentialIds?: string[];
}

/**
 * Generates the Android `resolve_profile_and_credentials` preflight deeplink.
 *
 * Format: privateid://init?sessionToken={}&policyId={}&rpId={}&returnURL={}
 *         &credentialIdList={csv}&action=resolve_profile_and_credentials
 *
 * This is Android's analogue of iOS's `ultrapass://start`: a **scan-free** check that reports
 * whether a credential for this RP already exists on the device, so the browser learns whether
 * to register or authenticate instead of guessing. The app documents it as the call to use
 * "instead of `VERIFY_CREDENTIALS` before register/authenticate".
 *
 * No biometric is captured — the face scan happens later, during the WebAuthn ceremony itself.
 * Both `rpId` and `returnURL` are mandatory: `SessionInitActivity` logs
 * "RESOLVE_PROFILE_AND_CREDENTIALS requires returnURL + rpId" and finishes silently otherwise.
 *
 * Parse the outcome with {@link parseReturnURLStatus}, which maps
 * `status=authenticate` → `mode: 'verify'` and `status=register` → `mode: 'enroll'`.
 *
 * @param params - Resolve parameters
 * @returns Android deeplink URL
 * @throws {Error} If rpId or returnURL is missing, which the app would silently ignore
 *
 * @example
 * ```typescript
 * const deeplink = generateAndroidResolveDeeplink({
 *   apiKey: 'your-api-key',
 *   policyId: 'mobile',
 *   rpId: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   credentialIds: ['AAA...', 'BBB...']
 * });
 * ```
 */
export function generateAndroidResolveDeeplink(params: AndroidResolveParams): string {
  // Fail loudly rather than emitting a deeplink the app drops on the floor without a callback,
  // which would strand the caller waiting for a redirect that never comes.
  if (!params.rpId) {
    throw new Error(
      'rpId is required for the Android resolve preflight — SessionInitActivity ignores the action without it.'
    );
  }

  if (!params.returnURL) {
    throw new Error(
      'returnURL is required for the Android resolve preflight — SessionInitActivity ignores the action without it.'
    );
  }

  const queryParams = new URLSearchParams();
  queryParams.set('sessionToken', params.apiKey);
  queryParams.set('policyId', params.policyId);
  queryParams.set('rpId', params.rpId);
  queryParams.set('returnURL', params.returnURL);

  // The app reads `credentialIdList` as CSV, falling back to singular `credentialId`.
  const credentialIds = params.credentialIds?.filter(id => !!id);
  if (credentialIds && credentialIds.length > 0) {
    queryParams.set('credentialIdList', credentialIds.join(','));
  }

  queryParams.set('action', ANDROID_ACTIONS.RESOLVE_PROFILE_AND_CREDENTIALS);

  return `privateid://init?${queryParams.toString()}`;
}

/**
 * Builds return URL with state parameters as query params
 *
 * This provides a fallback mechanism if sessionStorage doesn't persist
 * across the deeplink redirect (iOS Safari edge case). The operation type,
 * username, and apiKey are embedded in the return URL itself.
 *
 * Based on iOS dev reference implementation which uses URL params as backup.
 *
 * @param baseURL - Base return URL
 * @param operation - Operation type ('enroll' or 'verify')
 * @param params - Additional parameters to include
 * @returns Enhanced return URL with query parameters
 *
 * @example
 * ```typescript
 * const returnURL = buildReturnURLWithParams(
 *   'https://example.com/callback',
 *   'enroll',
 *   { username: 'alice', apiKey: 'test-key' }
 * );
 * // Returns: https://example.com/callback?op=enroll&username=alice&apiKey=test-key
 * ```
 */
export function buildReturnURLWithParams(
  baseURL: string,
  operation: 'enroll' | 'verify',
  params: { username?: string; apiKey?: string; verification?: string }
): string {
  try {
    const url = new URL(baseURL);

    // Add operation type
    url.searchParams.set('op', operation);

    // Add username if provided
    if (params.username) {
      url.searchParams.set('username', params.username);
    }

    // Add apiKey if provided (used for JWE verification on callback)
    if (params.apiKey) {
      url.searchParams.set('apiKey', params.apiKey);
    }

    // Add verification mode if provided (critical for backend verification)
    if (params.verification) {
      url.searchParams.set('verification', params.verification);
    }

    return url.toString();
  } catch (error) {
    // If URL parsing fails, return the base URL unchanged
    // This prevents breaking the flow if an invalid URL is provided
    console.error('[FIDO2-JS] Failed to parse return URL:', baseURL, error);
    return baseURL;
  }
}

/**
 * Encodes custom options as Base64 binary for iOS
 *
 * iOS expects options in the 32-byte binary format (same as PRF salt):
 * - Byte 0: 0xFF (magic byte)
 * - Byte 1: flags bitfield (checkLiveness, checkAge, etc.)
 * - Byte 2: age threshold value
 * - Bytes 3-31: reserved (zeros)
 *
 * This MUST match the format used by the POC reference implementation.
 *
 * @param options - Custom options object
 * @returns Base64-encoded binary string (32 bytes)
 *
 * @example
 * ```typescript
 * const encoded = encodeOptionsForIOS({
 *   checkLiveness: true,
 *   checkAge: true,
 *   ageThreshold: 21
 * });
 * // Returns base64 of: [0xFF, 0x07, 21, 0, 0, ..., 0]
 * ```
 */
export function encodeOptionsForIOS(options: CustomOptions): string {
  // Create 32-byte binary buffer (same format as PRF salt)
  const salt = new Uint8Array(32);

  // Byte 0: Magic byte
  salt[0] = 0xFF;

  // Byte 1: Build options flags bitfield
  let optionsFlags = 0;

  if (options.checkLiveness) {
    optionsFlags |= 1 << 0; // OptionFlag::CheckLiveness = 1 << 0
  }
  if (options.checkAge) {
    optionsFlags |= 1 << 1; // OptionFlag::CheckAge = 1 << 1
  }
  if (options.checkAgeAboveThreshold) {
    optionsFlags |= 1 << 2; // OptionFlag::CheckAgeAboveThreshold = 1 << 2
  }
  if (options.checkGeoLocation) {
    optionsFlags |= 1 << 3; // OptionFlag::CheckGeoLocation = 1 << 3
  }

  salt[1] = optionsFlags;

  // Byte 2: Set age threshold (default to 0 if not specified)
  salt[2] = (options.ageThreshold ?? 0) & 0xff;

  // Bytes 3-31 are already zero (default for Uint8Array)

  // Convert binary to base64 (matching POC implementation)
  let binaryString = '';
  for (let i = 0; i < salt.length; i++) {
    binaryString += String.fromCharCode(salt[i]);
  }

  return btoa(binaryString);
}

/**
 * Generates enrollment deeplink for the specified platform
 *
 * @param platform - Target platform ('iOS' or 'Android')
 * @param params - Enrollment parameters
 * @returns Platform-specific deeplink URL
 *
 * @throws {Error} If platform is not supported
 *
 * @example
 * ```typescript
 * const platform = detectPlatform();
 * const deeplink = generateEnrollmentDeeplink(platform, {
 *   username: 'john.doe',
 *   rp: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   policyId: 'mobile',
 *   apiKey: 'your-api-key'
 * });
 * ```
 */
export function generateEnrollmentDeeplink(
  platform: Platform,
  params: EnrollmentDeeplinkParams
): string {
  switch (platform) {
    case 'iOS':
      // Enhance return URL with state params (fallback for sessionStorage)
      const enhancedReturnURL = buildReturnURLWithParams(
        params.returnURL,
        'enroll',
        {
          username: params.username,
          apiKey: params.apiKey
        }
      );

      return generateIOSEnrollmentDeeplink({
        ...params,
        returnURL: enhancedReturnURL
      });
    case 'Android':
      // Android uses init deeplink for both enrollment and verification
      return generateAndroidInitDeeplink(params.apiKey, params.policyId);
    default:
      throw new Error(
        `Deeplink generation not supported for platform: ${platform}. ` +
          'Mobile deeplink flow is only available on iOS and Android.'
      );
  }
}

/**
 * Generates verification deeplink for the specified platform
 *
 * @param platform - Target platform ('iOS' or 'Android')
 * @param params - Verification parameters
 * @returns Platform-specific deeplink URL
 *
 * @throws {Error} If platform is not supported
 *
 * @example
 * ```typescript
 * const platform = detectPlatform();
 * const deeplink = generateVerificationDeeplink(platform, {
 *   rpId: 'example.com',
 *   returnURL: 'https://example.com/callback',
 *   policyId: 'mobile',
 *   apiKey: 'your-api-key',
 *   options: { checkLiveness: true }
 * });
 * ```
 */
export function generateVerificationDeeplink(
  platform: Platform,
  params: VerificationDeeplinkParams
): string {
  switch (platform) {
    case 'iOS':
      // Enhance return URL with state params (fallback for sessionStorage)
      const enhancedReturnURL = buildReturnURLWithParams(
        params.returnURL,
        'verify',
        {
          apiKey: params.apiKey,
          verification: params.verification || 'default' // Default to backend verification
        }
      );

      return generateIOSVerificationDeeplink({
        ...params,
        returnURL: enhancedReturnURL
      });
    case 'Android':
      // Android uses init deeplink for both enrollment and verification
      return generateAndroidInitDeeplink(params.apiKey, params.policyId);
    default:
      throw new Error(
        `Deeplink generation not supported for platform: ${platform}. ` +
          'Mobile deeplink flow is only available on iOS and Android.'
      );
  }
}

/**
 * Checks if deeplink flow is supported on the current platform
 *
 * @param platform - Platform to check
 * @returns true if deeplink flow is supported
 *
 * @example
 * ```typescript
 * const platform = detectPlatform();
 * if (isDeeplinkSupported(platform)) {
 *   // Use mobile deeplink flow
 * } else {
 *   // Use direct WebAuthn flow
 * }
 * ```
 */
export function isDeeplinkSupported(platform: Platform): boolean {
  return platform === 'iOS' || platform === 'Android';
}

/**
 * Parses return URL status from query parameters
 *
 * After the mobile app completes the biometric capture, it redirects back
 * to the return URL with a status query parameter:
 * - No status param = success
 * - ?status=error = biometric verification failed
 * - ?status=cancelled = user cancelled the operation
 *
 * Android's `resolve_profile_and_credentials` preflight reuses the same `status` param for its
 * own outcomes (`authenticate`, `register`, `deactivated`, `resolved_not_found`). Those are
 * recognised here and folded into `mode`, so a resolve callback drives the same
 * operation-selection path as the iOS `start` flow.
 *
 * @param url - URL to parse (defaults to current window.location)
 * @returns Status object with success flag and optional error message
 *
 * @example
 * ```typescript
 * // After redirect from mobile app
 * const status = parseReturnURLStatus();
 * if (!status.success) {
 *   console.error('Biometric failed:', status.error);
 * }
 * ```
 */
export function parseReturnURLStatus(url?: string): {
  success: boolean;
  error?: string;
  cancelled?: boolean;
  /**
   * Which operation UltraPass actually ran, as reported by the `start` flow appending
   * `mode=verify` / `mode=enroll` to the return URL. Absent on the legacy hosts.
   */
  mode?: 'enroll' | 'verify';
  /**
   * Machine-readable failure reason, e.g. `credential_invalid` when `verifyOnly=true` was sent
   * and no local credential matched.
   */
  reason?: string;
  /**
   * Outcome of Android's `resolve_profile_and_credentials` preflight, when the callback came
   * from one. No biometric has been captured at this point — the scan happens later, during
   * the WebAuthn ceremony.
   */
  resolve?: {
    status: 'authenticate' | 'register' | 'deactivated' | 'resolved_not_found';
    /** The matching credential the app found on the device, for `authenticate`. */
    credentialId?: string;
    /** Owning UltraPass profile, when the app resolved one. */
    profileId?: string;
  };
} {
  const urlToParse = url || (typeof window !== 'undefined' ? window.location.href : '');

  try {
    const urlObj = new URL(urlToParse);
    const status = urlObj.searchParams.get('status');

    // The unified `start` flow reports back which operation it performed. This is the app's
    // own account of what happened, so it outranks anything the browser guessed beforehand.
    const rawMode = urlObj.searchParams.get('mode');
    const mode = rawMode === 'enroll' || rawMode === 'verify' ? rawMode : undefined;
    const reason = urlObj.searchParams.get('reason') || undefined;

    if (!status) {
      // No status parameter means success
      return { success: true, mode };
    }

    // ── Android resolve preflight outcomes ──────────────────────────────────────────────
    // `authenticate` / `register` are successful resolutions, not ceremony results. Mapping
    // them onto `mode` lets the callback reuse the same operation-selection logic as iOS
    // `start`, which is the point: on both platforms the app decides, not the browser.
    if (status === 'authenticate' || status === 'register') {
      const credentialId = urlObj.searchParams.get('credentialId') || undefined;
      const profileId = urlObj.searchParams.get('profileId') || undefined;

      return {
        success: true,
        mode: status === 'authenticate' ? 'verify' : 'enroll',
        resolve: { status, credentialId, profileId }
      };
    }

    if (status === 'deactivated') {
      // The owning profile was auto-deactivated for inactivity. Not a retryable failure —
      // the user has to reactivate in UltraPass (tier-1 document + face).
      const message = urlObj.searchParams.get('message');

      return {
        success: false,
        reason: reason || 'profile_deactivated',
        error:
          message ||
          'This UltraPass profile has been deactivated for inactivity and must be reactivated in the UltraPass app before signing in.',
        resolve: {
          status,
          credentialId: urlObj.searchParams.get('credentialId') || undefined,
          profileId: urlObj.searchParams.get('profileId') || undefined
        }
      };
    }

    if (status === 'resolved_not_found') {
      return {
        success: false,
        reason,
        error:
          reason === 'initialization_failed'
            ? 'UltraPass could not initialize on this device, so credentials could not be resolved.'
            : 'UltraPass could not resolve any credential for this relying party on this device.',
        resolve: { status }
      };
    }

    if (status === 'error') {
      // Try to get more detailed error info from URL params
      const errorCode = urlObj.searchParams.get('errorCode');
      const errorMsg = urlObj.searchParams.get('errorMessage');

      // `credential_invalid` is a distinct, actionable outcome rather than a biometric failure,
      // and it has two quite different causes:
      //
      // 1. UltraPass found no passkey for this RP at all (verifyOnly=true suppressed the
      //    silent enroll fallback that would otherwise have kicked in).
      // 2. The passkey exists in UltraPass's authenticator, but no *face enrollment* is bound
      //    to it — `resolveCredentialByIds` needs a keychain enrollment carrying an embedding
      //    for that credential ID, and gives up when there is none
      //    (the app's credential resolver → "Please register again").
      //
      // Case 2 is the one that looks like a bug: routing correctly chose verify (the return URL
      // carries mode=verify), then resolution failed inside the app. Both are fixed by
      // registering again on the device, so the message says so rather than guessing which.
      if (reason === 'credential_invalid') {
        return {
          success: false,
          reason,
          mode,
          error:
            'UltraPass could not use a credential for this site on this device. Either no ' +
            'passkey is registered here, or the registered passkey has no face enrollment ' +
            'bound to it. Register again on this device to fix it.'
        };
      }

      let detailedError = 'Biometric verification failed in the UltraPass app.';

      if (errorMsg) {
        detailedError += ` Details: ${errorMsg}`;
      } else if (errorCode) {
        detailedError += ` Error code: ${errorCode}`;
      }

      return {
        success: false,
        error: detailedError,
        reason,
        mode
      };
    }

    if (status === 'cancelled') {
      return {
        success: false,
        cancelled: true,
        error: 'Operation was cancelled by the user.',
        reason,
        mode
      };
    }

    // Unknown status
    return {
      success: false,
      error: `Unknown status: ${status}`,
      reason,
      mode
    };
  } catch (error) {
    // URL parsing failed
    return {
      success: false,
      error: `Failed to parse return URL: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}
